// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/serialize";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion } from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import type { Brand as BrandFilter } from "../../CatalogSearch";
import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import { AllBrands } from "./AllBrands/AllBrands";
import { BrandSearch } from "./BrandSearch/BrandSearch";
import { PopularBrands } from "./PopularBrands/PopularBrands";

type Brand = Pick<Types.BrandContextualFilters, "id" | "esResultCount"> & {
  brand: Pick<Types.Brand, "id" | "name" | "logo">;
};
type NonContextualBrand = Pick<Types.Brand, "id" | "name" | "logo">;

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isDefaultExpanded?: boolean;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
  isContextual?: boolean;
  contextualData?: Brand[];
}

const Brands = ({
  isBold,
  logFilterEvent,
  isDefaultExpanded,
  customAccordionStyle,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const { activeBrands, setActiveBrands } = useCatalogSearch();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const isBrandActive = (id: string): boolean =>
    activeBrands.some(activeBrand => activeBrand.id === id);

  const onBrandSelect = (brandSelected: BrandFilter) => {
    if (!brandSelected?.id) {
      return null;
    }

    const distanceFromTop = window?.scrollY;
    if (isBrandActive(brandSelected.id)) {
      setActiveBrands(activeBrands.filter(brand => brand.id !== brandSelected.id));
    } else {
      logFilterEvent(brandSelected.name, FilterType.BRAND);
      setActiveBrands([...activeBrands, brandSelected]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const onAccordionChange = (isOpen: boolean) => {
    setIsAccordionOpen(isOpen);
  };

  if (isContextual && (contextualData?.length ?? 0) === 0) {
    return null;
  }

  return (
    <Accordion
      css={customAccordionStyle}
      data-e2e={e2eAttributes.brandsFilter}
      isDefaultExpanded={isDefaultExpanded || activeBrands?.length > 0 || isAccordionOpen}
      onChange={onAccordionChange}
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Brands)}
          count={activeBrands?.length}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <BrandSearch
        isBrandActive={isBrandActive}
        onBrandSelect={onBrandSelect}
        isContextual={isContextual}
      />

      <PopularBrands
        isBrandActive={isBrandActive}
        onBrandSelect={onBrandSelect}
        isContextual={isContextual}
        contextualData={contextualData}
      />

      <AllBrands
        isBrandActive={isBrandActive}
        onBrandSelect={onBrandSelect}
        isContextual={isContextual}
        contextualData={contextualData}
      />
    </Accordion>
  );
};

export { Brands, type Brand, type NonContextualBrand };
